import React, { useEffect } from 'react';
import styled from 'styled-components';
import { 
  FaBriefcase, FaBuilding, FaMapMarkerAlt, FaClock, FaExternalLinkAlt, 
  FaArrowRight
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const PageContainer = styled.div`
  margin-top: 180px;
  background: #ffffff;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-top: 155px;
  }


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

/* ========== HERO ========== */
const HeroSection = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #3730A3 50%, #0b0b77 100%);
  color: white;
  padding: 2.5rem 5%;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: 'Times New Roman', serif;
  font-size: 2.5rem; font-weight: 800; margin-bottom: 0.5rem;
  @media (max-width: 768px) { font-size: 1.8rem; }
`;

const HeroDesc = styled.p`
  font-family: 'Times New Roman', serif;
  font-size: 1rem; opacity: 0.85; max-width: 700px; margin: 0 auto;
`;

/* ========== CONTAINER ========== */
const Container = styled.div`
  max-width: 1000px; margin: 0 auto; padding: 2.5rem 5% 3rem;
`;

/* ========== INTRO TEXT ========== */
const IntroText = styled.p`
  font-family: 'Times New Roman', serif;
  color: #4a4a6a; font-size: 1rem; line-height: 1.7; margin-bottom: 2rem;
  text-align: center;
`;

/* ========== JOB LIST ========== */
const JobList = styled.div`
  display: flex; flex-direction: column; gap: 1rem; margin-bottom: 2rem;
`;

const JobCard = styled.a`
  background: white; border-radius: 12px; padding: 1.3rem 1.5rem; text-decoration: none;
  border: 1px solid #f0f0f5; display: flex; align-items: center; gap: 1.5rem;
  transition: all 0.3s ease;

  &:hover { 
    border-color: #3730A3; 
    box-shadow: 0 4px 20px rgba(55,48,163,0.06);
  }

  @media (max-width: 700px) { flex-direction: column; align-items: flex-start; gap: 0.8rem; }
`;

const JobLeft = styled.div`
  flex: 1; min-width: 200px;
`;

const JobTitle = styled.h3`
  font-family: 'Times New Roman', serif;
  color: #1a1a2e; font-size: 1.05rem; font-weight: 700; margin-bottom: 0.3rem;
`;

const JobCompany = styled.p`
  font-family: 'Times New Roman', serif;
  color: #3730A3; font-size: 0.9rem; font-weight: 600;
`;

const JobMiddle = styled.div`
  display: flex; flex-direction: column; gap: 0.3rem; min-width: 160px;
  span { 
    font-family: 'Times New Roman', serif;
    display: flex; align-items: center; gap: 0.4rem; color: #4a4a6a; font-size: 0.85rem; 
    svg { color: #3730A3; font-size: 0.8rem; flex-shrink: 0; } 
  }
`;

const JobSkills = styled.div`
  display: flex; gap: 0.4rem; flex-wrap: wrap;
  span { 
    font-family: 'Times New Roman', serif;
    color: #4a4a6a; font-size: 0.8rem;
    &:not(:last-child)::after { content: ','; }
  }
`;

const JobRight = styled.div`
  flex-shrink: 0;
`;

const ApplyButton = styled.span`
  font-family: 'Times New Roman', serif;
  background: #3730A3; color: white; padding: 0.5rem 1.2rem; border-radius: 25px;
  font-size: 0.85rem; font-weight: 600; display: inline-flex; align-items: center; gap: 0.3rem;
  white-space: nowrap;
  &:hover { background: #0b0b77; }
`;

/* ========== NOTE ========== */
const NoteCard = styled.div`
  background: #fafbff; border-radius: 12px; padding: 1.5rem; text-align: center;
  border: 1px solid #f0f0f5; margin-top: 2rem;
  p { 
    font-family: 'Times New Roman', serif;
    color: #4a4a6a; font-size: 0.95rem; line-height: 1.6; 
  }
  a {
    font-family: 'Times New Roman', serif;
    color: #3730A3; font-weight: 600; text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

const JobOpportunities = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const jobs = [
    { 
      title: "Medical Coder - CCS Certified", 
      company: "Omega Healthcare", 
      location: "Hyderabad, India", 
      type: "Full-time",
      skills: "CCS, ICD-10, Inpatient Coding",
      link: "https://www.linkedin.com/jobs/" 
    },
    { 
      title: "Senior CDI Specialist", 
      company: "Optum (UnitedHealth Group)", 
      location: "Bangalore, India", 
      type: "Full-time",
      skills: "CDI, CDIP, DRG Validation",
      link: "https://www.linkedin.com/jobs/" 
    },
    { 
      title: "Certified Professional Coder", 
      company: "GeBBS Healthcare", 
      location: "Mumbai, India", 
      type: "Full-time",
      skills: "CPC, Outpatient, E&M Coding",
      link: "https://www.linkedin.com/jobs/" 
    },
    { 
      title: "Inpatient DRG Coder", 
      company: "Cognizant Technologies", 
      location: "Chennai, India", 
      type: "Full-time",
      skills: "DRG, ICD-10-PCS, IPPS",
      link: "https://www.linkedin.com/jobs/" 
    },
    { 
      title: "HCC Risk Adjustment Coder", 
      company: "Deloitte", 
      location: "Remote, India", 
      type: "Full-time",
      skills: "HCC, CRC, Medicare Risk Adjustment",
      link: "https://www.linkedin.com/jobs/" 
    },
    { 
      title: "Medical Coding Auditor", 
      company: "R1 RCM", 
      location: "Delhi NCR, India", 
      type: "Full-time",
      skills: "Auditing, CPMA, Compliance",
      link: "https://www.linkedin.com/jobs/" 
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>Job Opportunities</HeroTitle>
        <HeroDesc>Find the latest medical coding jobs from top healthcare companies</HeroDesc>
      </HeroSection>

      <Container>
        <IntroText>
          Browse through the latest job openings in medical coding and healthcare documentation. 
          These positions are posted by various companies in the market. Click on any job to apply directly.
        </IntroText>

        <JobList>
          {jobs.map((job, i) => (
            <JobCard key={i} href={job.link} target="_blank" rel="noopener noreferrer">
              <JobLeft>
                <JobTitle>{job.title}</JobTitle>
                <JobCompany>{job.company}</JobCompany>
              </JobLeft>
              
              <JobMiddle>
                <span><FaMapMarkerAlt /> {job.location}</span>
                <span><FaClock /> {job.type}</span>
                <JobSkills><span>{job.skills}</span></JobSkills>
              </JobMiddle>
              
              <JobRight>
                <ApplyButton>Apply Now <FaExternalLinkAlt /></ApplyButton>
              </JobRight>
            </JobCard>
          ))}
        </JobList>

        <NoteCard>
          <p>
            <strong>Note:</strong> These job listings are from external sources. Learn Thru Us Academy does not guarantee placement 
            but shares available opportunities for your convenience. Always verify details on the company's official website.
          </p>
        </NoteCard>
      </Container>
    </PageContainer>
  );
};

export default JobOpportunities;